import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { AuthService } from '../../services/auth.service';
import { LoginRequest } from '../../models/user.model';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CardModule,
    MessageModule
  ],
  template: `
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8">
        <div class="text-center">
          <div class="mx-auto w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mb-6">
            <i class="pi pi-comments text-white text-2xl"></i>
          </div>
          <h2 class="text-center text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
            Ask IMC RAG
          </h2>
          <p class="mt-3 text-center text-base text-gray-600 font-medium">
            Your intelligent document assistant
          </p>
        </div>
        
        <p-card styleClass="shadow-2xl border-0 rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="space-y-7 p-2">
            <div class="form-group">
              <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
                <i class="pi pi-envelope mr-2 text-emerald-600"></i>Email Address
              </label>
              <div class="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  pInputText
                  formControlName="email"
                  placeholder="Enter your email address"
                  class="w-full h-12 px-4 pr-10 text-sm bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent hover:border-gray-400 transition-all duration-200"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <i class="pi pi-user text-gray-400 text-lg"></i>
                </div>
              </div>
              <small 
                *ngIf="loginForm.get('email')?.invalid && loginForm.get('email')?.touched"
                class="flex items-center mt-2 text-red-600 text-xs font-medium"
              >
                <i class="pi pi-exclamation-triangle mr-1"></i>
                Please enter a valid email address
              </small>
            </div>

            <div class="form-group">
              <label for="password" class="block text-sm font-semibold text-gray-700 mb-2">
                <i class="pi pi-key mr-2 text-emerald-600"></i>Password
              </label>
              <div class="relative">
                <p-password
                  id="password"
                  name="password"
                  formControlName="password"
                  [feedback]="false"
                  [toggleMask]="true"
                  placeholder="Enter your password"
                  styleClass="custom-password-input w-full"
                  inputStyleClass="w-full h-12 px-4 pr-12 text-sm bg-white border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent hover:border-gray-400 transition-all duration-200"
                ></p-password>
              </div>
              <small 
                *ngIf="loginForm.get('password')?.invalid && loginForm.get('password')?.touched"
                class="flex items-center mt-2 text-red-600 text-xs font-medium"
              >
                <i class="pi pi-exclamation-triangle mr-1"></i>
                Password is required
              </small>
            </div>

            <div *ngIf="errorMessage" class="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
              <div class="flex">
                <div class="flex-shrink-0">
                  <i class="pi pi-times-circle text-red-400"></i>
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
                </div>
              </div>
            </div>

            <div class="pt-2">
              <p-button
                type="submit"
                label="Start Chatting"
                [loading]="isLoading"
                loadingIcon="pi pi-spinner pi-spin"
                [disabled]="loginForm.invalid || isLoading"
                styleClass="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-base rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 border-0 focus:outline-none focus:ring-4 focus:ring-emerald-300"
                severity="primary"
              >
                <ng-template pTemplate="content">
                  <span class="flex items-center justify-center">
                    <i class="pi pi-comments mr-2" *ngIf="!isLoading"></i>
                    <span>{{ isLoading ? 'Signing In...' : 'Start Chatting' }}</span>
                  </span>
                </ng-template>
              </p-button>
            </div>
          </form>
        </p-card>

        <div class="text-center bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50">
          <p class="text-sm text-gray-600 font-medium">
            <i class="pi pi-info-circle mr-2 text-emerald-600"></i>
            Demo User Account
          </p>
          <p class="text-xs text-gray-500 mt-1 font-mono">
            nickj&#64;infocision.com / TestPassword123#
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    /* Allow Tailwind classes to work on inputs */
    ::ng-deep .p-inputtext {
      /* Remove most overrides to let Tailwind classes work */
    }
    
    /* Password input styling */
    .custom-password-input ::ng-deep .p-password {
      width: 100% !important;
    }
    
    .custom-password-input ::ng-deep .p-password-input {
      /* Remove overrides to let Tailwind inputStyleClass work */
    }
    
    /* Enhanced eye icon styling */
    .custom-password-input ::ng-deep .p-password-toggle {
      position: absolute !important;
      right: 12px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      width: 32px !important;
      height: 32px !important;
      border: none !important;
      background: rgba(16, 185, 129, 0.1) !important;
      border-radius: 6px !important;
      color: #10b981 !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 16px !important;
    }
    
    .custom-password-input ::ng-deep .p-password-toggle:hover {
      background: rgba(16, 185, 129, 0.2) !important;
      color: #047857 !important;
      transform: translateY(-50%) scale(1.05) !important;
    }
    
    .custom-password-input ::ng-deep .p-password-toggle:active {
      transform: translateY(-50%) scale(0.95) !important;
    }
    
    /* Button styling - let Tailwind do most of the work but add enhancements */
    ::ng-deep .p-button {
      /* Remove most overrides to let Tailwind styleClass work */
      position: relative !important;
      overflow: hidden !important;
    }
    
    /* Add button shine effect */
    ::ng-deep .p-button::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: -100% !important;
      width: 100% !important;
      height: 100% !important;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent) !important;
      transition: left 0.5s !important;
    }
    
    ::ng-deep .p-button:hover::before {
      left: 100% !important;
    }
    
    /* Ensure button text and icons are white */
    ::ng-deep .p-button .p-button-label {
      color: white !important;
      font-weight: 700 !important;
      z-index: 1 !important;
      position: relative !important;
    }
    
    ::ng-deep .p-button .p-button-icon {
      color: white !important;
      z-index: 1 !important;
      position: relative !important;
    }
    
    /* Loading state */
    ::ng-deep .p-button .p-button-loading-icon {
      color: white !important;
    }
    
    /* Enhanced card styling */
    ::ng-deep .p-card {
      border: 1px solid rgba(229, 231, 235, 0.3) !important;
      border-radius: 1rem !important;
      backdrop-filter: blur(10px) !important;
      background: rgba(255, 255, 255, 0.85) !important;
    }
    
    ::ng-deep .p-card .p-card-body {
      padding: 2rem !important;
    }
    
    ::ng-deep .p-card .p-card-content {
      padding: 0 !important;
    }
    
    /* Form group styling */
    .form-group {
      position: relative;
    }
    
    .form-group label {
      font-weight: 600 !important;
      color: #374151 !important;
      margin-bottom: 0.5rem !important;
      display: flex !important;
      align-items: center !important;
    }
    
    .form-group label i {
      margin-right: 0.5rem !important;
    }
    
    /* Input focus effects */
    .form-group input:focus + .absolute i {
      color: #10b981 !important;
      transform: scale(1.1) !important;
    }
    
    /* Input error styling */
    .form-group input.ng-invalid.ng-touched {
      border-color: #ef4444 !important;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
    }
    
    /* Icon styling */
    .pi {
      line-height: 1 !important;
    }
    
    /* Responsive adjustments */
    @media (max-width: 768px) {
      ::ng-deep .p-card .p-card-body {
        padding: 1.5rem !important;
      }
    }
  `]
})
export class LoginComponent {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const credentials: LoginRequest = this.loginForm.value;

      this.authService.login(credentials).subscribe({
        next: () => {
          this.router.navigate(['/chat']);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.error || 'Login failed. Please try again.';
        }
      });
    }
  }
} 